<script lang="ts">
  import { viewport } from '../lib/stores/viewport';
  import Icon from './Icon.svelte';
  import { router } from '@inertiajs/svelte';
  import type { Project, ProjectStatus, ProjectsByStatus } from '../lib/types';
  import { categoryColors } from '../lib/utils';
  import {
    isMonthRailOpen,
    monthRailData,
    closeMonthRail,
    draggedProject,
    openProjectRail,
    requestProjectMove,
  } from '../lib/stores/ui';

  interface Props {
    projectsByMonth: Record<string, ProjectsByStatus>;
    projectsByWeek?: Record<string, ProjectsByStatus>;
  }

  let { projectsByMonth, projectsByWeek = {} }: Props = $props();

  // Derive from store
  let isOpen = $derived($isMonthRailOpen);
  let data = $derived($monthRailData);

  // Determine if we're in week mode
  let isWeekMode = $derived(!!data?.weekKey);

  // Derive projects grouped by status from live props
  let projectsByStatus = $derived.by(() => {
    if (!data) return null;
    // Use week data if weekKey is provided, otherwise month data
    const source = data.weekKey ? projectsByWeek[data.weekKey] : projectsByMonth[data.monthKey];
    if (!source) return { active: [], planned: [], done: [] };
    return {
      active: source.active || [],
      planned: source.planned || [],
      done: source.done || [],
    };
  });

  // Total count
  let totalCount = $derived(
    projectsByStatus
      ? projectsByStatus.active.length +
          projectsByStatus.planned.length +
          projectsByStatus.done.length
      : 0,
  );

  // Status config (only the statuses we display)
  type DisplayStatus = 'active' | 'planned' | 'done';
  const statusConfig: { key: DisplayStatus; label: string; icon: string; bgColor: string }[] = [
    { key: 'active', label: 'Active', icon: '🔄', bgColor: 'bg-amber-50' },
    { key: 'planned', label: 'Planned', icon: '📋', bgColor: 'bg-blue-50' },
    { key: 'done', label: 'Done', icon: '✅', bgColor: 'bg-green-50' },
  ];

  // Drag state for accepting drops
  let dropTargetStatus = $state<ProjectStatus | null>(null);

  function handleDragOver(e: DragEvent, status: ProjectStatus) {
    e.preventDefault();
    if ($draggedProject && data) {
      dropTargetStatus = status;
    }
  }

  function handleDragLeave(e: DragEvent) {
    const relatedTarget = e.relatedTarget as Node;
    const currentTarget = e.currentTarget as Node;
    if (!currentTarget.contains(relatedTarget)) {
      dropTargetStatus = null;
    }
  }

  function handleDrop(e: DragEvent, status: ProjectStatus) {
    e.preventDefault();
    dropTargetStatus = null;

    // Only process if a project (not an event) is being dragged
    if (!$draggedProject) return;

    const projectId =
      e.dataTransfer?.getData('application/project') || e.dataTransfer?.getData('text/plain');
    if (projectId && data) {
      if (data.weekKey) {
        requestProjectMove(projectId, $draggedProject, 'week', { weekKey: data.weekKey, status });
      } else {
        requestProjectMove(projectId, $draggedProject, 'month', {
          monthKey: data.monthKey,
          status,
        });
      }
    }
  }

  // Dragging projects FROM this rail
  let draggingProjectId = $state<string | null>(null);

  function handleDragStart(e: DragEvent, project: Project) {
    draggingProjectId = project.id;
    draggedProject.set(project);
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', String(project.id));
    }
  }

  function handleDragEnd() {
    draggingProjectId = null;
    draggedProject.set(null);
  }
</script>

{#if isOpen && data && projectsByStatus}
  <aside class="month-rail" class:in-sheet={$viewport.isMobile} data-sheet-fill>
    <!-- Header -->
    <header class="rail-header" data-sheet-drag-handle>
      <div class="header-title">
        <span class="header-icon">📅</span>
        <h2 data-sheet-title>{data.monthLabel}</h2>
        <span class="header-count">{totalCount}</span>
      </div>
      <button
        type="button"
        class="close-btn"
        onclick={closeMonthRail}
        title="Close panel"
        data-sheet-close
      >
        {#if $viewport.isMobile}<Icon name="close" />{:else}✕{/if}
      </button>
    </header>

    <!-- Scrollable content with status groups -->
    <div class="rail-content">
      {#each statusConfig as status}
        {@const projects = projectsByStatus[status.key]}
        {#if projects.length > 0}
          <div class="status-group">
            <div class="status-header {status.bgColor}">
              <span class="status-icon">{status.icon}</span>
              <span class="status-label">{status.label}</span>
              <span class="status-count">{projects.length}</span>
            </div>
            <div
              class="project-list"
              class:drop-target={dropTargetStatus === status.key}
              role="group"
              ondragover={(e) => handleDragOver(e, status.key)}
              ondragleave={handleDragLeave}
              ondrop={(e) => handleDrop(e, status.key)}
            >
              {#each projects as project (project.id)}
                <button
                  type="button"
                  class="project-card {categoryColors[project.category]}"
                  class:dragging={draggingProjectId === project.id}
                  draggable={!$viewport.isMobile}
                  ondragstart={(e) => handleDragStart(e, project)}
                  ondragend={handleDragEnd}
                  onclick={() => openProjectRail(project.id)}
                >
                  <div class="card-content">
                    {#if project.isPriority}
                      <span class="star">★</span>
                    {/if}
                    <span class="card-title">{project.title}</span>
                    {#if project.multiWeek && project.totalWeeks}
                      <span class="card-span">{project.totalWeeks}w</span>
                    {/if}
                  </div>
                </button>
              {/each}
            </div>
          </div>
        {/if}
      {/each}

      {#if totalCount === 0}
        <div class="empty-state">
          <span class="empty-icon">📭</span>
          <span class="empty-text">No projects this {isWeekMode ? 'week' : 'month'}</span>
        </div>
      {/if}
    </div>

    <!-- Footer hint -->
    <footer class="rail-footer">Click to edit • Drag to reorder ←</footer>
  </aside>
{/if}

<style>
  .month-rail {
    width: 14rem;
    background: #f9fafb;
    border-left: 1px solid #e5e7eb;
    display: flex;
    flex-direction: column;
    flex-shrink: 0;
  }

  .rail-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.5rem;
    border-bottom: 1px solid #e5e7eb;
    background: white;
  }

  .header-title {
    display: flex;
    align-items: center;
    gap: 0.375rem;
  }

  .header-icon {
    font-size: 0.875rem;
  }

  .header-title h2 {
    font-size: 0.75rem;
    font-weight: 700;
    color: #374151;
  }

  .header-count {
    font-size: 0.625rem;
    color: #6b7280;
    font-weight: 500;
    background: #f3f4f6;
    padding: 0.125rem 0.375rem;
    border-radius: 9999px;
  }

  .close-btn {
    width: 1.5rem;
    height: 1.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.75rem;
    color: #9ca3af;
    background: none;
    border: none;
    border-radius: 0.25rem;
    cursor: pointer;
    transition: all 0.15s;
  }

  .close-btn:hover {
    color: #374151;
    background: #f3f4f6;
  }

  .rail-content {
    flex: 1;
    overflow-y: auto;
    padding: 0.375rem;
  }

  .status-group {
    margin-bottom: 0.5rem;
  }

  .status-group:last-child {
    margin-bottom: 0;
  }

  .status-header {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.25rem 0.375rem;
    border-radius: 0.25rem;
    margin-bottom: 0.25rem;
  }

  .status-icon {
    font-size: 0.625rem;
  }

  .status-label {
    font-size: 0.625rem;
    font-weight: 600;
    color: #374151;
  }

  .status-count {
    font-size: 0.5625rem;
    color: #6b7280;
    margin-left: auto;
  }

  .project-list {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    padding: 0.25rem;
    border-radius: 0.25rem;
    transition: all 0.15s;
  }

  .project-list.drop-target {
    background: #dbeafe;
    box-shadow: inset 0 0 0 2px #3b82f6;
  }

  .project-card {
    display: flex;
    align-items: center;
    padding: 0.25rem 0.375rem;
    border-radius: 0.25rem;
    font-size: 0.625rem;
    border: 1px solid transparent;
    cursor: grab;
    text-align: left;
    transition: all 0.15s;
  }

  .project-card:hover {
    opacity: 0.85;
  }

  .project-card.dragging {
    opacity: 0.5;
    cursor: grabbing;
  }

  .card-content {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    min-width: 0;
    flex: 1;
  }

  .star {
    color: #facc15;
    font-size: 0.5625rem;
    flex-shrink: 0;
  }

  .card-title {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: #1f2937;
    min-width: 0;
  }

  .card-span {
    flex-shrink: 0;
    font-size: 0.5625rem;
    font-weight: 600;
    font-family: ui-monospace, monospace;
    color: #1d4ed8;
    background: rgba(59, 130, 246, 0.15);
    padding: 0.0625rem 0.25rem;
    border-radius: 0.125rem;
    letter-spacing: -0.02em;
  }

  .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    color: #9ca3af;
    gap: 0.375rem;
    padding: 2rem;
  }

  .empty-icon {
    font-size: 1.5rem;
  }

  .empty-text {
    font-size: 0.625rem;
  }

  .rail-footer {
    padding: 0.375rem 0.5rem;
    border-top: 1px solid #e5e7eb;
    background: white;
    font-size: 0.5625rem;
    color: #9ca3af;
    text-align: center;
  }

  /* Mobile: inside the Dashboard's rail bottom sheet. */
  .month-rail.in-sheet {
    flex: 1 1 auto;
    min-height: 0;
    border-left: none;
  }

  .month-rail.in-sheet .rail-content {
    min-height: 0;
    overscroll-behavior: contain;
  }

  .month-rail.in-sheet .header-icon {
    font-size: 1.0625rem;
  }
</style>
